import { useGetPrecedingFilters } from 'features/drilldown/data/DrilldownFilterContext'
import {
  EnrichedOntologyFieldDefinition,
  OntologyRolodexEntryType,
  RD_QUERY_LIMIT,
  useHistogramQueries,
  useOntology,
} from 'features/dynamicOntology'
import {
  RolodexEntryQueryBody,
  useRolodexEntries,
  useRolodexHistogram,
} from 'features/rolodex'
import flatten from 'lodash/flatten'
import isNil from 'lodash/isNil'
import reduce from 'lodash/reduce'
import startsWith from 'lodash/startsWith'
import { useMemo } from 'react'
import {
  RolodexEntryType,
  RolodexHistogramAggregationOp,
  RolodexHistogramFilter,
  RolodexHistogramQuery,
  RolodexHistogramResponse,
} from 'types'

export type DrilldownHistogram = Record<string, number>

type RolodexHistogramCast = RolodexHistogramQuery['select'][number]['cast']
export function histogramSelect(
  groupingType: OntologyRolodexEntryType,
  aggregationType: OntologyRolodexEntryType,
  groupingColumn: string = 'id',
  aggregationColumn: string = 'id',
  histogramGroupByPropertyKey: 'key' | 'field' = 'field',
  aggregationPropertyKey: 'key' | 'field' = 'field',
  op: RolodexHistogramAggregationOp = 'count',
  aggCast?: RolodexHistogramCast
): RolodexHistogramQuery['select'] {
  return [
    {
      [histogramGroupByPropertyKey]: groupingColumn,
      type: groupingType,
      group_by: true,
      label: 'group_id',
      order_by: true,
    },
    {
      [aggregationPropertyKey]: aggregationColumn,
      type: aggregationType,
      op,
      label: 'value',
      cast: aggCast,
    },
  ]
}

export function extractHistoResult(
  histoResponse: RolodexHistogramResponse | undefined
): DrilldownHistogram {
  const histoData = histoResponse ?? []
  return Object.fromEntries(
    histoData
      .map((histoResultEntry): [string | undefined, number] => {
        const id = histoResultEntry['group_id'] as string | undefined
        const value = histoResultEntry['value'] as number
        return [id, value]
      })
      .filter(([id]) => !isNil(id))
      // Naive handling of invalid response values due to bad data
      // 1960s, 1990s, no dashes, etc
      .filter(([id]) => {
        const idAsString = id
        return !(
          idAsString === '20250206' ||
          startsWith(idAsString, '196') ||
          startsWith(idAsString, '199') ||
          startsWith(idAsString, '2010') ||
          startsWith(idAsString, '2011') ||
          startsWith(idAsString, '2012') ||
          startsWith(idAsString, '2013') ||
          startsWith(idAsString, '2014') ||
          startsWith(idAsString, '2015') ||
          startsWith(idAsString, '203')
        )
      })
  )
}

export function useFilteredEntryQuery(type: OntologyRolodexEntryType): {
  query: RolodexEntryQueryBody
  isLoading: boolean
} {
  const {
    queries: [rdHistoQuery],
    isLoading,
  } = useHistogramQueries({
    select: [{ type }],
    filters: useGetPrecedingFilters(),
    queryType: type,
  })
  return useMemo(
    () => ({
      query: {
        type: rdHistoQuery.type,
        links: rdHistoQuery.links,
        filters: rdHistoQuery.filters,
        tags: rdHistoQuery.tags,
        limit: rdHistoQuery.limit,
      },
      isLoading,
    }),
    [
      isLoading,
      rdHistoQuery.filters,
      rdHistoQuery.limit,
      rdHistoQuery.links,
      rdHistoQuery.tags,
      rdHistoQuery.type,
    ]
  )
}

export function useFilteredIds(
  type: OntologyRolodexEntryType,
  limit?: number
): {
  ids: ReadonlyArray<string>
  isLoading: boolean
} {
  const { query, isLoading: queryLoading } = useFilteredEntryQuery(type)
  const histoQuery = useMemo<ReadonlyArray<RolodexHistogramQuery>>(
    () =>
      query
        ? [
            {
              ...(query as RolodexHistogramQuery),
              select: [{ type, field: 'id', label: 'id' }],
              limit: limit ?? RD_QUERY_LIMIT,
            },
          ]
        : [],
    [limit, query, type]
  )
  const { data, isLoading: resultLoading } = useRolodexHistogram(histoQuery)
  return useMemo(
    () => ({
      ids: flatten(data).map((d) => d['id'] as string),
      isLoading: queryLoading || resultLoading,
    }),
    [data, queryLoading, resultLoading]
  )
}

/**
 * Helper to get a count of how many entries of a type are in scope given current filters
 *
 * @param type - The type of entry to count
 * @param additionalFilters - Additional filters to apply in addition to the ones from above the panel
 * @returns An object containing the count and a loading state
 */
export function useFilteredEntryCount(
  type: OntologyRolodexEntryType,
  additionalFilters?: ReadonlyArray<RolodexHistogramFilter>
): {
  count: number
  isLoading: boolean
} {
  // TODO - Rolodex now has a count endpoint, so we should use that instead of the histogram one
  const {
    histograms: [histogram],
    isLoading,
  } = useDrilldownHistograms({
    groupingType: type,
    aggregationType: type,
    groupingColumn: 'id',
    aggregationColumn: 'id',
    histogramGroupByPropertyKey: 'field',
    filters: additionalFilters,
  })

  const count = useMemo(() => Object.keys(histogram).length, [histogram])

  return useMemo(
    () => ({
      count,
      isLoading,
    }),
    [count, isLoading]
  )
}

export type UseHistogramsProps = {
  groupingType: OntologyRolodexEntryType
  aggregationType: OntologyRolodexEntryType
  groupingColumn?: string
  aggregationColumn?: string
  histogramGroupByPropertyKey?: 'key' | 'field'
  aggregationPropertyKey?: 'key' | 'field'
  filters?: ReadonlyArray<RolodexHistogramFilter>
  op?: RolodexHistogramAggregationOp
  aggCast?: RolodexHistogramCast
}

export function useDrilldownHistograms(
  props: UseHistogramsProps | ReadonlyArray<UseHistogramsProps>
): {
  isLoading: boolean
  histograms: ReadonlyArray<DrilldownHistogram>
} {
  // Normalize props to a memoized ReadonlyArray
  const propsArray: ReadonlyArray<UseHistogramsProps> = useMemo(
    () => (Array.isArray(props) ? props : [props]),
    [props]
  )

  const precedingFilters = useGetPrecedingFilters()

  // Build select arrays for each prop set
  const selects = useMemo(
    () =>
      propsArray.map(
        ({
          groupingType,
          aggregationType,
          groupingColumn = 'id',
          aggregationColumn = 'id',
          histogramGroupByPropertyKey = 'field',
          aggregationPropertyKey = 'field',
          op = 'count',
          aggCast,
        }) =>
          histogramSelect(
            groupingType,
            aggregationType,
            groupingColumn,
            aggregationColumn,
            histogramGroupByPropertyKey,
            aggregationPropertyKey,
            op,
            aggCast
          )
      ),
    [propsArray]
  )

  // Build filters arrays for each prop set
  const filtersArray = useMemo(
    () =>
      propsArray.map(({ filters = [] }) => [...precedingFilters, ...filters]),
    [propsArray, precedingFilters]
  )

  // Call useHistogramQueries with selects and filters
  const { queries: rdHistoQueries, isLoading: queriesLoading } =
    useHistogramQueries(
      useMemo(
        () =>
          selects.map((select, index) => ({
            select,
            filters: filtersArray[index],
          })),
        [selects, filtersArray]
      )
    )

  // Fetch histogram data for each query
  const { data: dataArrays, isLoading: dataLoading } =
    useRolodexHistogram(rdHistoQueries)

  // Extract counts from each data array, safely handling undefined dataArrays
  const histograms = useMemo(
    () =>
      rdHistoQueries.map((_, index) => {
        const data = dataArrays?.[index]
        return data ? extractHistoResult(data) : {}
      }),
    [dataArrays, rdHistoQueries]
  )

  // Combine loading states into a single flag
  const isLoading = queriesLoading || dataLoading

  // Return the single isLoading flag separately
  return useMemo(
    () => ({
      isLoading,
      histograms,
    }),
    [isLoading, histograms]
  )
}

/**
 * Helper function to get linked IDs from a set of IDs - for example,
 * given a list of inspection IDs, what are the units they're linked to?
 */
export function useGetLinkedIds(
  ids: ReadonlyArray<string>,
  toType: OntologyRolodexEntryType
): { links: ReadonlyMap<string, string>; isLoading: boolean } {
  const { entries, isLoading } = useRolodexEntries(
    useMemo(
      () =>
        ids.length
          ? [
              {
                ids: [...ids],
              },
            ]
          : [],
      [ids]
    )
  )

  return useMemo(() => {
    const recs = entries
      .map((e) => ({
        from: e.id,
        to:
          toType === e.type
            ? e.id
            : (e.tags[toType as RolodexEntryType] as string | undefined),
      }))
      .filter((e) => !!e.to)
    const resultMap = new Map<string, string>()
    recs.forEach(({ from, to }) => resultMap.set(from, to as string))
    return { links: resultMap, isLoading }
  }, [entries, isLoading, toType])
}

export function useFieldKeys(
  fields: ReadonlyArray<string>,
  filter?: (fieldDef: EnrichedOntologyFieldDefinition) => boolean
): Readonly<Record<string, { field?: string; key?: string; type?: string }>> {
  const { fields: schemaFields } = useOntology()
  return useMemo<
    Readonly<Record<string, { field?: string; key?: string; type?: string }>>
  >(
    () =>
      reduce(
        fields,
        (acc, field) => {
          const fieldDef = schemaFields?.[field]
          if (!fieldDef || (filter && !filter(fieldDef))) {
            return acc
          }
          const { keyOrField, key: fieldKey, entryType } = fieldDef
          return {
            ...acc,
            [field]: {
              [keyOrField === 'field' ? 'field' : 'key']: fieldKey,
              type: entryType,
            },
          }
        },
        {}
      ),
    [fields, filter, schemaFields]
  )
}
